import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { t } from '../../i18n';
import { Eye, EyeOff, Lock, User as UserIcon, X, Info, CheckCircle2, AlertTriangle, Loader, Mail, MessageSquare, ShieldCheck } from 'lucide-react';
import api from '../../services/api';
import FooterMentions from '../../components/common/FooterMentions';

function readCache() {
  try {
    const c = JSON.parse(localStorage.getItem('sv_cached_commerce') || 'null');
    return c || null;
  } catch { return null; }
}

// Cache de l'abonnement (écrit en session par AbonnementBanner) : permet
// d'afficher le compteur d'essai sur l'écran de connexion (hors session).
function readCachedAbo() {
  try { return JSON.parse(localStorage.getItem('sv_cached_abo') || 'null'); } catch { return null; }
}
function joursRestants(dateExp) {
  if (!dateExp) return null;
  const exp = new Date(dateExp); exp.setHours(0, 0, 0, 0);
  const now = new Date(); now.setHours(0, 0, 0, 0);
  return Math.round((exp - now) / 86400000);
}

function resolveLogoUrl(logo_url) {
  if (!logo_url) return null;
  if (logo_url.startsWith('http')) return logo_url;
  if (logo_url.startsWith('/uploads/')) {
    const base = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api')
      .replace(/\/api\/?$/, '');
    return base + logo_url;
  }
  return logo_url;
}

// Renvoie noir ou blanc selon la luminance, pour un texte lisible par-dessus
// une couleur d'accent.
function textColorOn(hex) {
  if (!hex) return '#ffffff';
  let h = hex.replace('#', '');
  if (h.length === 3) h = h.split('').map(c => c + c).join('');
  if (h.length !== 6) return '#ffffff';
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.6 ? '#0f172a' : '#ffffff';
}

// ── Modal « Mot de passe oublié » (email OU SMS) ────────────────
function ForgotPasswordModal({ onClose, identifiantInitial, accent = '#eab308', accentText = '#0f172a' }) {
  const focusAccent = {
    onFocus: e => { e.currentTarget.style.borderColor = accent; },
    onBlur: e => { e.currentTarget.style.borderColor = ''; },
  };
  const [etape, setEtape] = useState('saisie');   // 'saisie' | 'sms' | 'fait'
  const [identifiant, setIdentifiant] = useState(identifiantInitial || '');
  const [canal, setCanal] = useState('email');     // 'email' | 'sms'
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState(null);          // message final
  const [infoType, setInfoType] = useState('email'); // 'email' | 'sms' | 'admin'

  // étape SMS
  const [code, setCode] = useState('');
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');

  const demander = async (e) => {
    e.preventDefault();
    setError('');
    if (!identifiant.trim()) return setError('Saisis ton email ou ton numéro de téléphone.');
    setLoading(true);
    try {
      const res = await api.post('/auth/forgot-password', { identifiant: identifiant.trim(), canal });
      if (res.data.method === 'sms') {
        setEtape('sms');
        setInfo(res.data.message);
      } else {
        setInfoType(res.data.method === 'admin' ? 'admin' : 'email');
        setInfo(res.data.message);
        setEtape('fait');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Une erreur est survenue. Réessaie.');
    } finally {
      setLoading(false);
    }
  };

  const validerCode = async (e) => {
    e.preventDefault();
    setError('');
    if (code.trim().length < 4) return setError('Saisis le code reçu par SMS.');
    if (pwd.length < 6) return setError('Le mot de passe doit faire au moins 6 caractères.');
    if (pwd !== pwd2) return setError('Les deux mots de passe ne correspondent pas.');
    setLoading(true);
    try {
      const res = await api.post('/auth/reset-password-sms', {
        identifiant: identifiant.trim(), code: code.trim(), nouveauMotDePasse: pwd,
      });
      setInfoType('sms');
      setInfo(res.data.message || 'Mot de passe réinitialisé.');
      setEtape('fait');
    } catch (err) {
      setError(err.response?.data?.message || 'Code incorrect ou expiré.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
      role="dialog" aria-modal="true" aria-label="Mot de passe oublié"
      onClick={onClose}
    >
      <div className="bg-gray-900 border border-gray-800 rounded-2xl w-full max-w-md p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white">Mot de passe oublié</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200" aria-label="Fermer"><X size={20} /></button>
        </div>

        {etape === 'fait' ? (
          <div className="text-center py-2">
            {infoType === 'sms' ? <CheckCircle2 size={40} className="mx-auto text-green-400 mb-3" />
              : infoType === 'email' ? <CheckCircle2 size={40} className="mx-auto text-green-400 mb-3" />
              : <Info size={40} className="mx-auto text-blue-400 mb-3" />}
            <p className="text-gray-300 text-sm">{info}</p>
            <button onClick={onClose} className="mt-5 w-full bg-gray-800 hover:bg-gray-700 text-white py-2.5 rounded-lg transition">Compris</button>
          </div>
        ) : etape === 'sms' ? (
          <form onSubmit={validerCode} className="space-y-4">
            <p className="text-gray-400 text-sm">{info}</p>
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Code reçu par SMS</label>
              <input
                value={code}
                onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputMode="numeric"
                placeholder="6 chiffres"
                {...focusAccent}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 tracking-widest text-center text-lg focus:outline-none"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Nouveau mot de passe</label>
              <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="Min. 6 caractères" {...focusAccent}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:outline-none" autoComplete="new-password" />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Confirmer</label>
              <input type="password" value={pwd2} onChange={e => setPwd2(e.target.value)} placeholder="Re-saisis le mot de passe" {...focusAccent}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:outline-none" autoComplete="new-password" />
            </div>
            {error && <div className="bg-red-900/30 border border-red-700 text-red-400 rounded-lg px-4 py-2.5 text-sm">{error}</div>}
            <button type="submit" disabled={loading}
              style={{ backgroundColor: accent, color: accentText }}
              className="w-full inline-flex items-center justify-center gap-2 font-bold py-2.5 rounded-lg transition hover:opacity-90 disabled:opacity-60">
              {loading && <Loader size={16} className="animate-spin" />}
              {loading ? 'Validation…' : 'Réinitialiser'}
            </button>
            <button type="button" onClick={() => { setEtape('saisie'); setError(''); }} className="w-full text-xs text-gray-500 hover:text-gray-300">
              ← Recommencer
            </button>
          </form>
        ) : (
          <form onSubmit={demander} className="space-y-4">
            <p className="text-gray-400 text-sm">
              Saisis ton email ou ton numéro de téléphone, puis choisis comment recevoir
              les instructions de réinitialisation.
            </p>
            <div>
              <label className="block text-sm text-gray-400 mb-1.5">Email ou téléphone</label>
              <input
                value={identifiant}
                onChange={e => setIdentifiant(e.target.value)}
                {...focusAccent}
                className="w-full bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-3 focus:outline-none"
                placeholder="email@gmail.com ou 6xx xx xx xx"
                required autoFocus
              />
            </div>
            {/* Choix du canal */}
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setCanal('email')}
                style={canal === 'email' ? { backgroundColor: accent + '26', borderColor: accent, color: accent } : undefined}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm border transition ${canal === 'email' ? '' : 'border-gray-700 text-gray-400 hover:bg-gray-800'}`}>
                <Mail size={15} /> Par email
              </button>
              <button type="button" onClick={() => setCanal('sms')}
                style={canal === 'sms' ? { backgroundColor: accent + '26', borderColor: accent, color: accent } : undefined}
                className={`flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm border transition ${canal === 'sms' ? '' : 'border-gray-700 text-gray-400 hover:bg-gray-800'}`}>
                <MessageSquare size={15} /> Par SMS
              </button>
            </div>
            {error && <div className="bg-red-900/30 border border-red-700 text-red-400 rounded-lg px-4 py-2.5 text-sm">{error}</div>}
            <button type="submit" disabled={loading}
              style={{ backgroundColor: accent, color: accentText }}
              className="w-full inline-flex items-center justify-center gap-2 font-bold py-2.5 rounded-lg transition hover:opacity-90 disabled:opacity-60">
              {loading && <Loader size={16} className="animate-spin" />}
              {loading ? 'Envoi…' : 'Envoyer'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

export default function Login() {
  const [form, setForm] = useState({ identifiant: '', password: '' });
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [capsOn, setCapsOn] = useState(false);
  const [showForgot, setShowForgot] = useState(false);
  const [mfaChallenge, setMfaChallenge] = useState(null); // challengeToken si 2FA requise
  const [code, setCode] = useState('');
  const [sessionExpiree, setSessionExpiree] = useState(false);
  const { login, login2fa } = useAuth();
  const navigate = useNavigate();

  // Message « session expirée » si on arrive ici suite à un time-out d'inactivité.
  useEffect(() => {
    try {
      if (sessionStorage.getItem('sv_session_expiree')) {
        setSessionExpiree(true);
        sessionStorage.removeItem('sv_session_expiree');
      }
    } catch { /* ignore */ }
  }, []);

  const cache = useMemo(readCache, []);
  const logoUrl = resolveLogoUrl(cache?.logo_url);
  const nomCommerce = cache?.nom || '';
  const couleurFond = cache?.couleur_principale || '#0f172a';
  const couleurIcones = cache?.couleur_secondaire || '#64748b';
  // Thème FIXE de l'écran de connexion commerce (demandé) :
  // fond bleu ciel, boutons/accents dorés, champs de saisie blancs.
  const FOND_LOGIN = '#7dd3fc';     // bleu ciel
  const accent = '#d4af37';         // doré
  const accentText = textColorOn(accent); // texte sombre, lisible sur le doré

  // Compteur d'essai (depuis le cache de la dernière session)
  const abo = useMemo(readCachedAbo, []);
  const essaiJours = abo?.plan_code === 'essai' ? joursRestants(abo.date_expiration) : null;
  // Bord d'accent au focus des champs (sans dépendre d'une classe Tailwind fixe)
  const focusAccent = {
    onFocus: e => { e.currentTarget.style.borderColor = accent; },
    onBlur: e => { e.currentTarget.style.borderColor = ''; },
  };

  const majHandler = (e) => {
    if (typeof e.getModifierState === 'function') setCapsOn(e.getModifierState('CapsLock'));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const identifiant = form.identifiant.trim();
    if (!identifiant) return setError('Saisis ton email ou ton téléphone.');
    if (!form.password) return setError('Saisis ton mot de passe.');
    setLoading(true);
    try {
      const result = await login(identifiant, form.password);
      // 2FA active : on bascule sur l'étape de saisie du code, sans naviguer.
      if (result?.mfaRequired) {
        setMfaChallenge(result.challengeToken);
        setError('');
        return;
      }
      const user = result;
      if (user.role === 'vendeur') navigate('/vendeurs/ma-journee');
      else navigate('/dashboard');
    } catch (err) {
      if (err.response?.status === 429) {
        setError(err.response?.data?.message || 'Trop de tentatives. Réessaie dans quelques minutes.');
      } else {
        setError(err.response?.data?.message || t('auth.identifiantsIncorrects'));
      }
    } finally {
      setLoading(false);
    }
  };

  // Deuxième étape : validation du code TOTP (2FA).
  const handle2fa = async (e) => {
    e.preventDefault();
    setError('');
    if (!/^\d{6}$/.test(code)) return setError('Saisis le code à 6 chiffres.');
    setLoading(true);
    try {
      const user = await login2fa(mfaChallenge, code);
      if (user.role === 'vendeur') navigate('/vendeurs/ma-journee');
      else navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Code invalide ou expiré.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col p-4" style={{ backgroundColor: FOND_LOGIN }}>
      {/* Bande période d'essai (depuis le cache de la dernière session) */}
      {abo?.plan_code === 'essai' && essaiJours !== null && (
        <div className="relative z-10 -mx-4 -mt-4 mb-4 px-4 py-2 text-center text-sm font-medium border-b"
          style={essaiJours >= 0
            ? { backgroundColor: 'rgba(245,158,11,0.15)', borderColor: 'rgba(245,158,11,0.4)', color: '#fcd34d' }
            : { backgroundColor: 'rgba(239,68,68,0.15)', borderColor: 'rgba(239,68,68,0.4)', color: '#fca5a5' }}>
          {essaiJours > 1 ? `🎁 Période d'essai gratuite — ${essaiJours} jours restants`
            : essaiJours >= 0 ? `🎁 Période d'essai gratuite — dernier jour`
            : `⏳ Période d'essai terminée — passez en Premium`}
        </div>
      )}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full blur-3xl animate-pulse" style={{ backgroundColor: couleurFond, opacity: 0.15 }} />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: couleurIcones, opacity: 0.12 }} />
      </div>

      <div className="relative z-10 flex-1 flex items-center justify-center w-full">
        <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {logoUrl ? (
            <img src={logoUrl} alt={nomCommerce || 'Logo'}
              className="w-24 h-24 object-contain mx-auto mb-3 drop-shadow-2xl rounded-xl bg-white p-2"
              onError={e => { e.target.style.display = 'none'; }} />
          ) : (
            <img src="/logos-gc/app-icon-192.png" alt="GestiCom"
              className="w-24 h-24 object-contain mx-auto mb-3 drop-shadow-2xl rounded-xl" />
          )}
          {nomCommerce ? (
            <h1 className="text-2xl font-bold" style={{ color: couleurIcones }}>{nomCommerce}</h1>
          ) : (
            <h1 className="text-2xl font-bold text-slate-900">Connexion</h1>
          )}
          <p className="text-slate-800 font-medium mt-1">{t('auth.gestionCommerce')}</p>
        </div>

        <div className="bg-blue-950 border border-blue-900 rounded-2xl p-8 shadow-2xl">
          {sessionExpiree && (
            <div className="mb-5 flex items-center gap-2 px-4 py-3 bg-amber-900/30 border border-amber-700/50 rounded-xl text-sm text-amber-200" role="status">
              <AlertTriangle size={16} className="flex-shrink-0 text-amber-400" />
              <span>Votre session a expiré (inactivité). Reconnectez-vous.</span>
            </div>
          )}
          <h2 className="text-xl font-semibold text-white mb-1 text-center">{t('auth.connexion')}</h2>
          <p className="text-sm text-gray-400 mb-6 text-center">
            {mfaChallenge
              ? 'Saisissez le code de votre application d\'authentification'
              : 'Entrez vos identifiants pour accéder à votre espace'}
          </p>

          {mfaChallenge ? (
            <form onSubmit={handle2fa} className="space-y-5" noValidate>
              <div>
                <label htmlFor="login-2fa" className="block text-sm text-gray-400 mb-1.5">Code de vérification</label>
                <input
                  id="login-2fa"
                  value={code}
                  onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  inputMode="numeric"
                  autoFocus
                  {...focusAccent}
                  className="w-full bg-white border border-slate-300 text-slate-900 placeholder-slate-400 rounded-lg px-4 py-3 tracking-[0.5em] text-center text-lg focus:outline-none transition-colors"
                  placeholder="000000"
                />
              </div>
              {error && (
                <div className="bg-red-900/30 border border-red-700 text-red-400 rounded-lg px-4 py-3 text-sm" role="alert">{error}</div>
              )}
              <button type="submit" disabled={loading}
                style={{ backgroundColor: accent, color: accentText }}
                className="w-full inline-flex items-center justify-center gap-2 font-bold py-3 rounded-lg transition-all duration-200 hover:opacity-90 disabled:opacity-60 shadow-lg">
                {loading && <Loader size={16} className="animate-spin" />}
                {loading ? 'Vérification…' : 'Valider le code'}
              </button>
              <button type="button" onClick={() => { setMfaChallenge(null); setCode(''); setError(''); }}
                className="w-full text-xs text-gray-500 hover:text-gray-300">
                ← Retour à la connexion
              </button>
            </form>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5" noValidate>
            {/* Identifiant : email ou téléphone */}
            <div>
              <label htmlFor="login-id" className="block text-sm text-gray-400 mb-1.5">Email ou téléphone</label>
              <div className="relative">
                <UserIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  id="login-id"
                  type="text"
                  value={form.identifiant}
                  onChange={e => setForm({ ...form, identifiant: e.target.value })}
                  {...focusAccent}
                  className="w-full bg-white border border-slate-300 text-slate-900 placeholder-slate-400 rounded-lg pl-10 pr-4 py-3 focus:outline-none transition-colors"
                  placeholder="email@gmail.com ou 6xx xx xx xx"
                  required
                  autoComplete="username"
                />
              </div>
            </div>

            {/* Mot de passe */}
            <div>
              <label htmlFor="login-password" className="block text-sm text-gray-400 mb-1.5">{t('auth.motDePasse')}</label>
              <div className="relative">
                <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                <input
                  id="login-password"
                  type={showPwd ? 'text' : 'password'}
                  value={form.password}
                  onChange={e => setForm({ ...form, password: e.target.value })}
                  onKeyUp={majHandler}
                  onKeyDown={majHandler}
                  {...focusAccent}
                  className="w-full bg-white border border-slate-300 text-slate-900 placeholder-slate-400 rounded-lg pl-10 pr-10 py-3 focus:outline-none transition-colors"
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                />
                <button type="button" onClick={() => setShowPwd(!showPwd)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                  aria-label={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}>
                  {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {capsOn && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-400">
                  <AlertTriangle size={13} /> La touche Verr. Maj (Caps Lock) est activée.
                </p>
              )}
            </div>

            {error && (
              <div className="bg-red-900/30 border border-red-700 text-red-400 rounded-lg px-4 py-3 text-sm" role="alert">{error}</div>
            )}

            <button type="submit" disabled={loading}
              style={{ backgroundColor: accent, color: accentText }}
              className="w-full inline-flex items-center justify-center gap-2 font-bold py-3 rounded-lg transition-all duration-200 hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed shadow-lg">
              {loading && <Loader size={16} className="animate-spin" />}
              {loading ? t('auth.chargement') : t('auth.seConnecter')}
            </button>

            {/* Mot de passe oublié — sous le bouton de connexion */}
            <p className="text-center text-sm text-gray-400 pt-1">
              Vous avez oublié votre mot de passe ?{' '}
              <button type="button" onClick={() => setShowForgot(true)}
                style={{ color: accent }}
                className="font-medium hover:opacity-80 transition-opacity">
                Réinitialiser
              </button>
            </p>
          </form>
          )}
        </div>

        <div className="text-center mt-6">
          <button onClick={() => navigate('/superadmin/login')}
            className="inline-flex items-center gap-1.5 text-xs text-slate-700 hover:text-slate-900 font-medium transition-colors">
            <ShieldCheck size={13} /> Accès console Super Admin
          </button>
        </div>

        {nomCommerce && (
          <p className="text-center text-slate-700 text-xs mt-3">{nomCommerce} © {new Date().getFullYear()}</p>
        )}
        </div>
      </div>
      <FooterMentions variant="onlight" className="relative z-10 mt-4" />

      {showForgot && (
        <ForgotPasswordModal identifiantInitial={form.identifiant} accent={accent} accentText={accentText} onClose={() => setShowForgot(false)} />
      )}
    </div>
  );
}
